<template>
  <div :class="$style.container">
    <section v-if="!file">
      <b-upload
        v-model="file"
        multiple
        expanded
        drag-drop
        accept=".pdf,.docx,.jpg,.jpeg,.png,.xlsx,.pptx"
        :class="$style.uploadInput"
      >
        <section class="section">
          <div class="content has-text-centered">
            <p>
              <img src="@/assets/images/upload.png" :class="$style.uploadImg" />
            </p>
            <p :class="$style.uploadText">Drag and drop your file(s) here</p>
            <p :class="$style.uploadSubText">or <a>select file</a></p>
            <p :class="$style.uploadSubText">
              (.pdf .docx .xlsx .pptx .jpg .jpeg .png format, up to 50MB)
            </p>
          </div>
        </section>
      </b-upload>
    </section>
    <div :class="$style.successUpload" v-if="fileAttachment.length > 0">
      <div class="tags">
        <template>
          <span
            v-for="(file, index) in fileAttachment"
            :key="index"
            :class="$style.tagFileName"
          >
            <span :class="$style.textFileName"> {{ file.name }} </span>
            <button
              class="delete is-small"
              type="button"
              @click="deleteDropFile(index)"
            ></button>
          </span>
        </template>
      </div>
    </div>
    <br v-if="fileAttachment.length > 0" />
    <b-upload
      v-if="!isSuccess && !isLoading && !isFailed"
      v-model="file"
      multiple
      expanded
      drag-drop
      accept=".pdf,.docx,.jpg,.jpeg,.png,.xlsx,.pptx"
      :class="$style.uploadInput"
    >
      <section class="section">
        <div class="content has-text-centered">
          <p>
            <img src="@/assets/images/upload.png" :class="$style.uploadImg" />
          </p>
          <p :class="$style.uploadText">Drag and drop your file(s) here</p>
          <p :class="$style.uploadSubText">or <a>select file</a></p>
          <p :class="$style.uploadSubText">
            (.pdf .docx .xlsx .pptx .jpg .jpeg .png format, up to 50MB)
          </p>
        </div>
      </section>
    </b-upload>

    <section v-if="isSuccess && !isLoading && !isFailed">
      <b-upload
        v-model="file"
        accept=".pdf,.docx,.jpg,.jpeg,.png,.xlsx,.pptx"
        drag-drop
        multiple
        expanded
        :class="$style.uploadInput"
      >
        <section class="section">
          <div class="content has-text-centered">
            <p>
              <img
                src="@/assets/images/uploaded.png"
                :class="$style.uploadImg"
              />
            </p>
            <p :class="$style.uploadSubText">
              <a>Drag and drop your file(s) here</a>
            </p>
            <p :class="$style.uploadSubText">or <a>select file</a></p>
            <p :class="$style.uploadSubText">
              (.pdf .docx .xlsx .pptx .jpg .jpeg .png format, up to 50MB)
            </p>
          </div>
        </section>
      </b-upload>
    </section>

    <section v-if="isLoading">
      <UploadLoading :isLoading="isLoading" :isSuccess="isSuccess" />
    </section>

    <section v-if="isFailed & !isLoading">
      <UploadError :message="error" @retryUpload="handleRetry" />
    </section>
  </div>
</template>

<script lang="ts">
import { Prop, Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import UploadError from '@/components/uploader/UploadError.vue';
import UploadTimeout from '@/components/uploader/UploadTimeout.vue';
import UploadLoading from '@/components/uploader/UploadLoading.vue';
import {
  GetSignedUrlForUploadRequestPayload,
  GetSignedUrlForUploadResponsePayload,
  GetFileAttachmentPayload,
} from '@/store/modules/static-file/types/static-file.types';

@Component({
  components: {
    UploadError,
    UploadLoading,
    UploadTimeout,
  },
})
export default class GenericFileUploader extends Vue {
  public isLoading: boolean = false;
  public isSuccess: boolean = false;
  public isFailed: boolean = false;
  public error = '';
  public file: File[] = [];
  public fileSizeLimit: number = 50000000;

  @Prop({ default: [] })
  public fileAttachment!: GetFileAttachmentPayload[];

  public isFileDeleted: boolean = false;

  @Action('staticFile/getSignedUrlForUploads')
  public getSignedUrlForUploads!: (payload: {
    data: GetSignedUrlForUploadRequestPayload[];
  }) => void;

  @State((state) => state.staticFile.signedUrlData)
  public signedUrlData!: any;

  @Watch('file')
  public async onFileSelected() {
    if (this.file.length === 0) {
      return;
    }

    if (this.isFileDeleted) {
      this.isFileDeleted = false;
    }

    const oversizedFile = this.file.find((file: File) => {
      return file.size > this.fileSizeLimit;
    });

    if (oversizedFile) {
      this.error = 'File size exceeded the 50MB limit';
      this.isFailed = true;
      this.file.splice(-1);
      return;
    }

    return this.getSignedUrlForUploads({
      data: this.file.map((file: File) => {
        return {
          fileName: file.name,
          contentType: file.type,
        };
      }),
    });
  }

  @Watch('signedUrlData', { deep: true })
  public onGetSignedUrlData(
    signedUrls: GetSignedUrlForUploadResponsePayload[],
  ) {
    if (signedUrls) {
      this.uploadFileToSignedUrl(this.file, signedUrls);
      this.file = [];
    }
  }

  public handleRetry() {
    this.isFailed = false;
    this.isSuccess = false;
  }

  public deleteDropFile(index: number) {
    if (this.fileAttachment.length !== 0) {
      this.fileAttachment.splice(index, 1);
      this.isFileDeleted = true;
    }
  }

  public uploadFileToSignedUrl(
    selectedFiles: File[],
    signedUrls: GetSignedUrlForUploadResponsePayload[],
  ) {
    selectedFiles.forEach((file, fileIndex) => {
      const formData = new FormData();
      formData.append('file', file as Blob);
      // upload file to GCS
      return new Promise((resolve, reject) => {
        this.isLoading = true;
        this.$emit('fileUploading', this.isLoading);

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrls[fileIndex].signedUrl, true);

        xhr.onload = (e) => {
          this.isLoading = false;
          this.$emit('fileUploading', this.isLoading);
          this.isSuccess = true;
          const subFileName = signedUrls[fileIndex].storageUrl.substring(105);
          // update file attachment once file is uploaded
          this.fileAttachment.push({
            type: signedUrls[fileIndex].storageUrl,
            name: subFileName,
          });
          return resolve(signedUrls);
        };

        xhr.onerror = (error) => {
          this.isFailed = true;
          return reject(error);
        };

        xhr.send(file);
      });
    });
  }
}
</script>

<style lang="scss" module>
.successUpload {
  border: 1px solid $grey-border;
  padding: 6px;
}
.container {
  justify-content: center;
  display: flex;
  flex: 1;
  flex-direction: column;

  .uploadInput {
    width: 100%;
    background-color: #f3fbff;
    border-color: $primary;

    div {
      width: 100%;
    }
  }
}

.uploadImg {
  width: 64px;
}

.uploadText {
  &#{&}#{&} {
    font-size: 18px;
    font-weight: 700;
    line-height: 18px;
    color: #444f51;
  }
}

.uploadSubText {
  &#{&}#{&} {
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: #444f51;
  }
}
.tagFileName {
  background-color: #e2f1ff !important;
  color: #444f51 !important;
  font-size: 12px;
  font-weight: 400;
  overflow-wrap: anywhere;
  align-items: center;
  border-radius: 4px;
  display: inline-flex;
  height: 2em;
  -webkit-box-pack: center;
  justify-content: center;
  line-height: 1.5;
  -webkit-box-align: center;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  height: 100%;
  margin-left: 0.275rem;
}
.textFileName {
  padding-right: 5px;
}
</style>
